if (!customElements.get('dixie-story')) {
  class DixieStory extends HTMLElement {
    constructor() {
      super();
      this.scene = 0;
      this.detailStep = 0;
      this.locked = false;
      this.closed = false;
      this.pageLocked = false;
      this.touchStart = null;
      this.sceneCount = 0;
      this.transitionLock = 620;
      this.closeDuration = 500;
      this.seenKey = this.dataset.seenKey || 'peachys-dixie-story-seen-v1';
      this.handleCanvasClick = this.advance.bind(this);
      this.handleKeydown = this.onKeydown.bind(this);
      this.handleTouchStart = this.onTouchStart.bind(this);
      this.handleTouchEnd = this.onTouchEnd.bind(this);
      this.handleSkip = (event) => {
        event.stopPropagation();
        this.close();
      };
      this.handleSectionSelect = (event) => {
        if (event.target === this.closest('.shopify-section')) this.open(true);
      };
      this.handleBlockSelect = (event) => {
        const selectedScene = event.target?.closest?.('[data-dixie-scene]');
        if (!selectedScene || !this.contains(selectedScene)) return;
        const selectedIndex = this.scenes.indexOf(selectedScene);
        if (selectedIndex < 0) return;
        this.open(true);
        this.scene = selectedIndex;
        this.detailStep = 0;
        this.update();
      };
    }

    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.canvas = this.querySelector('[data-dixie-canvas]');
      this.skip = this.querySelector('[data-dixie-skip]');
      this.finalButton = this.querySelector('[data-dixie-close]');
      this.currentLabel = this.querySelector('[data-dixie-current]');
      this.wipe = this.querySelector('[data-dixie-wipe]');
      this.hint = this.querySelector('[data-dixie-hint]');
      this.hintLabel = this.querySelector('[data-dixie-hint-label]');
      this.scenes = Array.from(this.querySelectorAll('[data-dixie-scene]'));
      this.sceneCount = this.scenes.length;
      this.continueLabel = this.hintLabel?.textContent?.trim() || 'Tap to continue';
      this.finalLabel = this.dataset.finalLabel || 'Tap to enter the shop';
      this.previousBodyOverflow = '';
      this.previousHtmlOverflow = '';
      this.previousBodyTouchAction = '';

      const replay = new URLSearchParams(window.location.search).get('replay') === '1';
      const showEveryVisit = this.dataset.frequency === 'every_visit';
      let seen = false;

      try {
        seen = window.sessionStorage.getItem(this.seenKey) === '1';
      } catch (_error) {
        seen = false;
      }

      if (replay || showEveryVisit || !seen || window.Shopify?.designMode) {
        this.open();
      }

      document.addEventListener('shopify:section:select', this.handleSectionSelect);
      document.addEventListener('shopify:block:select', this.handleBlockSelect);
    }

    disconnectedCallback() {
      this.removeListeners();
      document.removeEventListener('shopify:section:select', this.handleSectionSelect);
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      this.unlockPage();
    }

    open(fromEditor = false) {
      const wasHidden = this.hidden;
      this.closed = false;
      this.scene = 0;
      this.detailStep = 0;
      this.classList.remove('pdx-closing');
      this.hidden = false;
      this.fromEditor = fromEditor;
      this.update();
      if (wasHidden) {
        this.lockPage();
        this.addListeners();
      }
      window.requestAnimationFrame(() => this.focus({ preventScroll: true }));
    }

    addListeners() {
      this.removeListeners();
      this.canvas?.addEventListener('click', this.handleCanvasClick);
      this.canvas?.addEventListener('touchstart', this.handleTouchStart, { passive: true });
      this.canvas?.addEventListener('touchend', this.handleTouchEnd, { passive: true });
      this.skip?.addEventListener('click', this.handleSkip);
      this.finalButton?.addEventListener('click', this.handleSkip);
      window.addEventListener('keydown', this.handleKeydown);
    }

    removeListeners() {
      this.canvas?.removeEventListener('click', this.handleCanvasClick);
      this.canvas?.removeEventListener('touchstart', this.handleTouchStart);
      this.canvas?.removeEventListener('touchend', this.handleTouchEnd);
      this.skip?.removeEventListener('click', this.handleSkip);
      this.finalButton?.removeEventListener('click', this.handleSkip);
      window.removeEventListener('keydown', this.handleKeydown);
    }

    lockPage() {
      if (this.pageLocked) return;
      this.previousBodyOverflow = document.body.style.overflow;
      this.previousHtmlOverflow = document.documentElement.style.overflow;
      this.previousBodyTouchAction = document.body.style.touchAction;
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
      this.pageLocked = true;
    }

    unlockPage() {
      if (!this.pageLocked) return;
      document.body.style.overflow = this.previousBodyOverflow;
      document.documentElement.style.overflow = this.previousHtmlOverflow;
      document.body.style.touchAction = this.previousBodyTouchAction;
      this.pageLocked = false;
    }

    setTransitionLock() {
      this.locked = true;
      window.setTimeout(() => {
        this.locked = false;
      }, this.transitionLock);
    }

    animateWipe() {
      if (!this.wipe) return;
      this.wipe.classList.remove('is-wiping');
      void this.wipe.offsetWidth;
      this.wipe.classList.add('is-wiping');
      window.setTimeout(() => this.wipe?.classList.remove('is-wiping'), 600);
    }

    goTo(next) {
      this.setTransitionLock();
      this.animateWipe();
      this.scene = next;
      this.detailStep = 0;
      this.update();
    }

    currentDetailLayers() {
      return Array.from(this.scenes[this.scene]?.querySelectorAll('[data-dixie-detail]') || []);
    }

    advance(event) {
      if (event?.target?.closest('[data-dixie-skip], [data-dixie-close]')) return;
      if (this.locked || this.closed) return;

      const detailLayers = this.currentDetailLayers();
      if (detailLayers.length && this.detailStep < detailLayers.length - 1) {
        this.setTransitionLock();
        this.detailStep += 1;
        this.update();
        return;
      }

      if (this.scene === this.sceneCount - 1) {
        this.close();
        return;
      }

      this.goTo(this.scene + 1);
    }

    back() {
      if (this.locked || this.closed) return;

      if (this.currentDetailLayers().length && this.detailStep > 0) {
        this.setTransitionLock();
        this.detailStep -= 1;
        this.update();
        return;
      }

      if (this.scene === 0) return;
      const previousIndex = this.scene - 1;
      const previousDetails = Array.from(
        this.scenes[previousIndex]?.querySelectorAll('[data-dixie-detail]') || []
      );
      this.goTo(previousIndex);
      if (previousDetails.length) {
        this.detailStep = previousDetails.length - 1;
        this.update();
      }
    }

    onKeydown(event) {
      if (event.key === ' ' || event.key === 'Spacebar' || event.key === 'Enter' || event.key === 'ArrowRight') {
        if (event.key !== 'ArrowRight' && event.target?.tagName === 'BUTTON') return;
        event.preventDefault();
        this.advance();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.back();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      }
    }

    onTouchStart(event) {
      const touch = event.touches[0];
      if (touch) this.touchStart = { x: touch.clientX, y: touch.clientY };
    }

    onTouchEnd(event) {
      const start = this.touchStart;
      this.touchStart = null;
      const touch = event.changedTouches[0];
      if (!start || !touch) return;

      const dx = touch.clientX - start.x;
      const dy = touch.clientY - start.y;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.4) {
        if (dx < 0) this.advance();
        else this.back();
      }
    }

    update() {
      this.scenes.forEach((scene, index) => {
        const active = index === this.scene;
        scene.classList.toggle('pdx-scene--active', active);
        scene.setAttribute('aria-hidden', String(!active));
      });

      this.currentDetailLayers().forEach((layer, index) => {
        layer.classList.toggle('is-on', index === this.detailStep);
      });

      this.scenes[this.scene]?.querySelectorAll('[data-dixie-detail-copy]').forEach((panel) => {
        const step = Number(panel.dataset.dixieDetailCopy || 0);
        panel.classList.toggle('is-on', step === this.detailStep);
      });

      this.querySelectorAll('[data-dixie-bar]').forEach((bar, index) => {
        bar.classList.toggle('is-on', index <= this.scene);
      });

      if (this.currentLabel) this.currentLabel.textContent = String(this.scene + 1).padStart(2, '0');
      if (this.hintLabel) {
        this.hintLabel.textContent =
          this.scene === this.sceneCount - 1 ? this.finalLabel : this.continueLabel;
      }
    }

    close() {
      if (this.closed) return;
      this.closed = true;
      this.classList.add('pdx-closing');
      this.removeListeners();

      if (!window.Shopify?.designMode || !this.fromEditor) {
        try {
          window.sessionStorage.setItem(this.seenKey, '1');
        } catch (_error) {
          // sessionStorage can be unavailable in strict private modes.
        }
      }

      window.setTimeout(() => {
        this.hidden = true;
        this.classList.remove('pdx-closing');
        this.unlockPage();
        window.scrollTo({ top: 0, behavior: 'auto' });
        const productTitle = document.querySelector('.product__title h1, .product__title');
        if (productTitle) {
          productTitle.setAttribute('tabindex', '-1');
          productTitle.focus({ preventScroll: true });
        }
      }, this.closeDuration);
    }
  }

  customElements.define('dixie-story', DixieStory);
}
